import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/Header";
import { OmniLogo } from "../components/OmniLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { usePageTitle } from "../lib/usePageTitle";
import { toast } from "sonner";
import {
  Upload, File as FileIcon, Trash2, Star, Download, Image as ImageIcon, FileText,
  Loader2, Folder, FolderPlus, Share2, Copy, Check, ChevronRight, Home,
} from "lucide-react";

function iconFor(mime) {
  if (mime?.startsWith("image/")) return ImageIcon;
  if (mime?.startsWith("text/")) return FileText;
  return FileIcon;
}
function colorFor(mime) {
  if (mime?.startsWith("image/")) return "#10B981";
  if (mime?.startsWith("text/")) return "#3b82f6";
  if (mime?.includes("pdf")) return "#dc2626";
  return "#F59E0B";
}
function formatSize(n) {
  if (!n) return "0 o";
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / 1024 / 1024).toFixed(2)} Mo`;
}

export default function OmniDrivePage() {
  usePageTitle("OmniDrive");
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState(["/"]);
  const [currentFolder, setCurrentFolder] = useState("/");
  const [load, setLoad] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [shareModal, setShareModal] = useState(null);
  const [copied, setCopied] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { navigate("/login", { state: { from: "/drive" } }); return; }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, navigate, currentFolder]);

  const reload = async () => {
    setLoad(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/drive/files", { params: { folder: currentFolder } }),
        api.get("/drive/folders"),
      ]);
      setFiles(a.data || []);
      setFolders(b.data.folders || ["/"]);
    } finally { setLoad(false); }
  };

  const onPick = () => inputRef.current?.click();
  const onFiles = async (e) => {
    const list = e.target.files;
    if (!list || list.length === 0) return;
    setUploading(true);
    try {
      for (const f of list) {
        if (f.size > 2 * 1024 * 1024) { toast.error(`${f.name} dépasse 2 Mo`); continue; }
        const b64 = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(String(r.result).split(",")[1]);
          r.onerror = reject;
          r.readAsDataURL(f);
        });
        const { data } = await api.post("/drive/upload", {
          name: f.name, mime: f.type || "application/octet-stream", content_b64: b64, size: f.size, folder: currentFolder,
        });
        setFiles((arr) => [data, ...arr]);
      }
      toast.success("Fichier(s) ajouté(s)");
    } catch {
      toast.error("Échec de l'envoi");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const download = async (id) => {
    const { data } = await api.get(`/drive/files/${id}/download`);
    const bin = atob(data.content_b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    const blob = new Blob([arr], { type: data.mime });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = data.name;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const toggleStar = async (id) => {
    const { data } = await api.post(`/drive/files/${id}/star`);
    setFiles((arr) => arr.map((x) => x.id === id ? { ...x, starred: data.starred } : x));
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce fichier ?")) return;
    await api.delete(`/drive/files/${id}`);
    setFiles((arr) => arr.filter((x) => x.id !== id));
  };

  const share = async (f) => {
    const { data } = await api.post(`/drive/files/${f.id}/share`);
    const fullUrl = `${window.location.origin}${data.public_url}`;
    setShareModal({ file: f, url: fullUrl, token: data.share_token });
    setFiles((arr) => arr.map((x) => x.id === f.id ? { ...x, share_token: data.share_token } : x));
  };

  const unshare = async (id) => {
    await api.post(`/drive/files/${id}/unshare`);
    setFiles((arr) => arr.map((x) => x.id === id ? { ...x, share_token: null } : x));
    setShareModal(null);
    toast.success("Lien révoqué");
  };

  const createFolder = async () => {
    const name = prompt("Nom du dossier :");
    if (!name) return;
    const path = currentFolder === "/" ? `/${name.trim()}` : `${currentFolder}/${name.trim()}`;
    try {
      await api.post("/drive/folders", { path });
      toast.success("Dossier créé");
      reload();
    } catch {
      toast.error("Création échouée");
    }
  };

  const breadcrumbs = currentFolder === "/" ? [] : currentFolder.split("/").filter(Boolean);
  // Folders that are direct children of currentFolder
  const childFolders = folders.filter((f) => {
    if (f === currentFolder) return false;
    if (currentFolder === "/") return f !== "/" && f.lastIndexOf("/") === 0;
    if (!f.startsWith(currentFolder + "/")) return false;
    return f.slice(currentFolder.length + 1).indexOf("/") === -1;
  });

  if (loading || !user || user === false) return null;

  return (
    <div className="min-h-screen flex flex-col" data-testid="drive-page">
      <Header compact />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
          <div className="flex items-center gap-3">
            <OmniLogo brand="drive" size={40} />
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">OmniDrive</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={createFolder} variant="outline" className="rounded-full" data-testid="drive-new-folder-btn">
              <FolderPlus size={15} className="mr-2" /> Nouveau dossier
            </Button>
            <Button onClick={onPick} disabled={uploading} className="rounded-full bg-[#F59E0B] hover:bg-[#D97706] text-white" data-testid="drive-upload-btn">
              {uploading ? <Loader2 className="animate-spin mr-2" size={15} /> : <Upload size={15} className="mr-2" />}
              {uploading ? "Envoi…" : "Importer"}
            </Button>
            <input ref={inputRef} type="file" multiple onChange={onFiles} className="hidden" data-testid="drive-file-input" />
          </div>
        </div>

        {/* Breadcrumbs */}
        <div className="flex items-center gap-1 text-sm text-gray-600 mb-6" data-testid="drive-breadcrumbs">
          <button onClick={() => setCurrentFolder("/")} className="inline-flex items-center gap-1 hover:text-black">
            <Home size={13} /> Racine
          </button>
          {breadcrumbs.map((b, i) => {
            const target = "/" + breadcrumbs.slice(0, i + 1).join("/");
            return (
              <span key={target} className="inline-flex items-center gap-1">
                <ChevronRight size={12} className="text-gray-300" />
                <button onClick={() => setCurrentFolder(target)} className="hover:text-black">{b}</button>
              </span>
            );
          })}
        </div>

        {load && <div className="text-gray-500">Chargement…</div>}
        {!load && childFolders.length === 0 && files.length === 0 && (
          <div onClick={onPick} className="border-2 border-dashed border-[var(--omni-border)] rounded-2xl p-16 text-center text-gray-500 hover:border-[#F59E0B] hover:text-[#F59E0B] transition cursor-pointer" data-testid="drive-empty">
            <Upload size={32} className="mx-auto mb-3 opacity-60" />
            <p className="font-medium">Cliquez ou déposez un fichier ici</p>
            <p className="text-xs mt-1">Tous formats acceptés, jusqu'à 2 Mo</p>
          </div>
        )}

        {/* Folders */}
        {childFolders.length > 0 && (
          <>
            <div className="text-xs font-bold uppercase text-gray-500 mb-2">Dossiers</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
              {childFolders.map((f) => (
                <button
                  key={f}
                  onClick={() => setCurrentFolder(f)}
                  data-testid={`drive-folder-${f.replace(/\//g, "-")}`}
                  className="border border-[var(--omni-border)] rounded-xl p-4 hover:bg-[var(--omni-surface)] flex items-center gap-3 text-left"
                >
                  <Folder size={24} className="text-[#F59E0B]" />
                  <span className="font-medium text-sm truncate">{f.split("/").pop()}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {/* Files */}
        {files.length > 0 && (
          <>
            <div className="text-xs font-bold uppercase text-gray-500 mb-2">Fichiers ({files.length})</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {files.map((f) => {
                const Icon = iconFor(f.mime);
                return (
                  <div key={f.id} className="border border-[var(--omni-border)] rounded-xl p-4 hover:shadow-md transition group" data-testid={`drive-file-${f.id}`}>
                    <div className="aspect-video rounded-lg flex items-center justify-center mb-3 relative" style={{ background: colorFor(f.mime) + "20" }}>
                      <Icon style={{ color: colorFor(f.mime) }} size={36} />
                      {f.share_token && (
                        <span className="absolute top-1 right-1 bg-emerald-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5">
                          <Share2 size={9} /> Partagé
                        </span>
                      )}
                    </div>
                    <div className="flex items-start gap-1 min-w-0">
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-sm truncate" title={f.name}>{f.name}</div>
                        <div className="text-xs text-gray-500">{formatSize(f.size)} · {new Date(f.created_at).toLocaleDateString()}</div>
                      </div>
                      <button onClick={() => toggleStar(f.id)} className={`p-1 ${f.starred ? "text-amber-500" : "text-gray-300 hover:text-amber-500"}`} data-testid={`drive-star-${f.id}`}>
                        <Star size={14} fill={f.starred ? "currentColor" : "none"} />
                      </button>
                    </div>
                    <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition flex-wrap">
                      <Button size="sm" variant="ghost" onClick={() => download(f.id)} className="h-7 px-2 text-xs" data-testid={`drive-download-${f.id}`}>
                        <Download size={12} className="mr-1" /> DL
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => share(f)} className="h-7 px-2 text-xs" data-testid={`drive-share-${f.id}`}>
                        <Share2 size={12} className="mr-1" /> {f.share_token ? "Lien" : "Partager"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(f.id)} className="h-7 px-2 text-xs text-red-600" data-testid={`drive-delete-${f.id}`}>
                        <Trash2 size={12} />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>

      {shareModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShareModal(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="drive-share-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <Share2 size={18} />
              </div>
              <div>
                <h3 className="font-bold">Partager "{shareModal.file.name}"</h3>
                <p className="text-xs text-gray-500">Toute personne avec ce lien pourra télécharger le fichier.</p>
              </div>
            </div>
            <div className="bg-[var(--omni-surface)] rounded-lg p-3 font-mono text-xs break-all mb-3" data-testid="drive-share-url">
              {shareModal.url}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => { navigator.clipboard.writeText(shareModal.url); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                className="rounded-full bg-black text-white"
                data-testid="drive-share-copy"
              >
                {copied ? <><Check size={13} className="mr-1" /> Copié</> : <><Copy size={13} className="mr-1" /> Copier</>}
              </Button>
              <Button onClick={() => unshare(shareModal.file.id)} variant="outline" className="rounded-full text-red-600" data-testid="drive-share-revoke">
                Révoquer
              </Button>
              <Button onClick={() => setShareModal(null)} variant="ghost" className="ml-auto">Fermer</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
